// combatpro/views/EffectsView.js
//
// Stage 74 (Effects View): migrated from the Stage 33 placeholder shell (which
// rendered state.effectsItems.map - a Stage 22 empty placeholder) to the legacy
// active-monster effect row (battletracker.html effects-block, 6415-6436).
//
// Faithful migration. Renders ONLY the active monster
// (state.currentCombatantIndex) into #vi-effects-mount; empty mount when no
// active monster - mirroring the per-monster view pattern used since Stage 66.
//
// Responsibilities (legacy parity):
//   - Effect <select>        options from EFFECTS_DB names; onChange -> updateEffect
//                            (CombatContext 641-648, reused unchanged).
//   - Started Round input    numeric; onChange -> updateEffectStart
//                            (additive CombatContext handler, mirrors legacy 7731-7735).
//   - Effect description      derived from EFFECTS_DB.find(...).desc. Legacy wrote
//                            this imperatively to effectInfo-${index} (7722); here it
//                            is declarative. No DOM writes.
//   - Rounds Left badge       derived. Legacy 7923-7924:
//                            duration === 0 ? "\u2014"
//                              : Math.max(0, (effectStartRound + duration) - currentRound)
//                            currentRound is state.currentRound (CombatContext 83).
//
// EFFECTS_DB is imported from the canonical pure-data module (Stage 74,
// effects/effectsDb.js). conditionsEngine is deliberately NOT used: it carries
// neither duration nor these descriptions. No optional chaining, no localStorage,
// no DOM manipulation, no em dashes in source (the badge glyph is '\u2014').
import React, { useContext } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { EFFECTS_DB } from '../effects/effectsDb';

function EffectsView() {
  const { state, updateEffect, updateEffectStart } = useContext(CombatContext);

  const index = state.currentCombatantIndex;
  const monster = (index !== null && index !== undefined && state.monsters)
    ? state.monsters[index]
    : null;

  // No active monster: empty mount, faithful to the per-monster view pattern.
  if (!monster) {
    return (
      <div className="vi-view" id="view-effects">
        <div id="vi-effects-mount"></div>
      </div>
    );
  }

  const effectName = monster.effect || 'None';
  const startRound = (monster.effectStartRound !== undefined && monster.effectStartRound !== null)
    ? monster.effectStartRound
    : 1;

  // Resolve the EFFECTS_DB entry (guarded; no optional chaining).
  let effectData = null;
  for (let i = 0; i < EFFECTS_DB.length; i++) {
    if (EFFECTS_DB[i].name === effectName) {
      effectData = EFFECTS_DB[i];
      break;
    }
  }

  // Effect description (legacy effectInfo, 7722): desc when matched, else ''.
  const effectDesc = effectData ? effectData.desc : '';

  // Rounds Left (legacy 7923-7924). duration 0 -> dash; otherwise clamp at 0.
  let roundsLeft;
  if (!effectData || effectData.duration === 0) {
    roundsLeft = '\u2014';
  } else {
    roundsLeft = Math.max(0, (startRound + effectData.duration) - state.currentRound);
  }

  return (
    <div className="vi-view" id="view-effects">
      <div id="vi-effects-mount">
        <div className="effects-block" id={'effects-block-' + index}>
          <div className="effect-row-label">Active Effect / Condition</div>

          <div className="effect-row">
            <div className="effect-field">
              <label htmlFor={'effect-' + index}>Effect</label>
              <select
                id={'effect-' + index}
                value={effectName}
                onChange={(e) => updateEffect(index, e.target.value)}
              >
                {EFFECTS_DB.map((e) => (
                  <option key={e.name} value={e.name}>{e.name}</option>
                ))}
              </select>
            </div>

            <div className="effect-field">
              <label htmlFor={'effectStart-' + index}>Started Round</label>
              <input
                id={'effectStart-' + index}
                type="number"
                value={startRound}
                onChange={(e) => updateEffectStart(index, parseInt(e.target.value, 10))}
              />
            </div>

            <div className="effect-field">
              <label>Rounds Left</label>
              <div
                className="rounds-badge"
                id={'effectRoundsLeft-' + index}
                style={{
                  width: '100%',
                  height: '38px',
                  lineHeight: '38px',
                  fontSize: '14px',
                  padding: '0 12px',
                  boxSizing: 'border-box',
                  textAlign: 'center'
                }}
              >
                {roundsLeft}
              </div>
            </div>
          </div>

          <div className="spell-info" id={'effectInfo-' + index}>{effectDesc}</div>
        </div>
      </div>
    </div>
  );
}

export default EffectsView;
